import {
  arcRowCount,
  arcSeats,
  curvedEdges,
  edgeAngle,
  heldStep,
  MIN_ARC_RADIUS,
  radiusForCurve,
} from "./arcs";

// Geometry is independent of event inventory. IDs never depend on display labels.
export const SCHEMA_VERSION = 2;
export const SEAT_PITCH = 38;
const MAX_OBJECT_SEATS = 2000;
export const MAX_ARC_DEGREES = 300;
export const clone = (value) => JSON.parse(JSON.stringify(value));
export function uid(prefix = "obj") {
  const bytes = window.crypto?.getRandomValues?.(new Uint8Array(8));
  if (!bytes) throw new Error("Secure random generation is required.");
  return `${prefix}_${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}
// Two decimals is finer than a seat can be placed and keeps drawings small.
// Rounding the size leaves mirrored sections exact mirrors.
const tidy = (n) => (Math.sign(n) * Math.round(Math.abs(n) * 100)) / 100 + 0;
export function rowLabel(index) {
  let label = "";
  for (let n = index; n >= 0; n = Math.floor(n / 26) - 1)
    label = String.fromCharCode(65 + (n % 26)) + label;
  return label;
}
// Row labels run from a starting letter or number. I and O read as 1 and 0 on
// a ticket, so letters skip them unless the organizer turns that off.
export function rowNamer({ rowFrom = "A", skipIO = true }) {
  const start = String(rowFrom).trim().toUpperCase();
  if (/^[0-9]+$/.test(start)) return (i) => String(Number(start) + i);
  if (!/^[A-Z]+$/.test(start))
    throw new Error("Start row labels at a letter or number.");
  let first = 0;
  for (const letter of start)
    first = first * 26 + letter.charCodeAt(0) - 64;
  const names = [];
  let next = first - 1;
  return (i) => {
    while (names.length <= i) {
      const name = rowLabel(next++);
      if (!skipIO || !/[IO]/.test(name) || name === start) names.push(name);
    }
    return names[i];
  };
}
export function emptyLayout(name = "Event venue") {
  return {
    schemaVersion: SCHEMA_VERSION,
    id: uid("layout"),
    name,
    width: 1200,
    height: 800,
    blocks: [],
    seats: [],
    markers: [],
  };
}
// Chairs in a half ring fit fewer to the same table, so they need more edge.
export const tableRadius = ({ chairs = 8, facing = false }) =>
  Math.ceil(
    Math.max(60, facing ? ((chairs - 1) * SEAT_PITCH) / (0.7 * Math.PI) - 30 : chairs * 7),
  );
export function generateBlock(settings) {
  return generateShape({ ...settings, shape: "grid" });
}
// The widest row of an arc, in degrees: edge to edge, or a held row's seats.
export function arcSpan(block) {
  const radius = Number(block.radius),
    rows = Number(block.rows),
    rowGap = Number(block.rowSpacing ?? SEAT_PITCH),
    seatGap = Number(block.seatSpacing ?? SEAT_PITCH);
  const spans = Array.from({ length: rows }, (_, r) => {
    const R = radius + r * rowGap;
    return Array.isArray(block.rowSeats)
      ? (block.rowSeats[r] - 1) * heldStep(R, block.rowPitch?.[r] ?? seatGap)
      : edgeAngle(block.to, R) - edgeAngle(block.from, R);
  });
  return Math.max(...spans);
}
// `wrapAllowed` lets an arc that already wraps past the limit be rebuilt as it is.
export function generateShape(settings, { wrapAllowed = MAX_ARC_DEGREES } = {}) {
  const {
    name,
    shape = "grid",
    numbering = "forward",
    formID = null,
  } = settings;
  const count = (key, fallback, max = 100) => {
    const n = Number(settings[key] ?? fallback);
    if (!Number.isInteger(n) || n < 1 || n > max)
      throw new Error(`Enter a whole ${key} count between 1 and ${max}.`);
    return n;
  };
  if (!name?.trim()) throw new Error("Enter a section or table name.");
  const objectID = settings.objectID || uid("obj");
  const block = {
    ...settings,
    id: objectID,
    objectID,
    shape,
    name: name.trim(),
    x: Number(settings.x ?? 180),
    y: Number(settings.y ?? 200),
    rotation: Number(settings.rotation || 0),
    formID,
  };
  const seats = [];
  const add = (
    suffix,
    x,
    y,
    row,
    column,
    rowText = "",
    label = String(column),
  ) =>
    seats.push({
      id: `${objectID}:${suffix}`,
      blockId: objectID,
      row,
      column,
      rowLabel: rowText,
      label,
      x,
      y,
      viewNote: "",
      formID,
      packageID: settings.sellAsPackage ? `${objectID}:pkg` : null,
    });
  const number = (c, total) =>
    String(
      numbering === "reverse"
        ? total - c
        : numbering === "odd"
          ? c * 2 + 1
          : numbering === "even"
            ? c * 2 + 2
            : c + 1,
    );
  if (shape === "grid" || shape === "arc") {
    const rows = count("rows", 5),
      columns = count("columns", 6);
    const rowName = rowNamer(settings);
    const rowOffset = Number(settings.rowOffset || 0);
    const spacing = (key) => {
      const n = Number(settings[key] ?? SEAT_PITCH);
      if (!Number.isFinite(n) || n < 30 || n > 200)
        throw new Error("Enter a spacing between 30 and 200.");
      return n;
    };
    const seatGap = spacing("seatSpacing"),
      rowGap = spacing("rowSpacing");
    block.skipIO = settings.skipIO ?? true;
    // The row part of an ID stays with its row when rows are added at the front,
    // while labels follow position.
    const place = (r, c, total, x, y) =>
      add(
        `r${r + 1 + rowOffset}:s${c + 1}`,
        x,
        y,
        r + 1,
        c + 1,
        rowName(r),
        number(c, total),
      );
    if (shape === "grid") {
      // Rows held to their own counts sit centred on the widest.
      const counts = Array.from({ length: rows }, (_, r) =>
        Array.isArray(settings.rowSeats) ? settings.rowSeats[r] || columns : columns,
      );
      const widest = Math.max(...counts);
      if (counts.reduce((a, b) => a + b, 0) > MAX_OBJECT_SEATS)
        throw new Error("Use at most 2,000 seats per object.");
      counts.forEach((count, r) => {
        for (let c = 0; c < count; c++)
          place(r, c, count, (c + (widest - count) / 2) * seatGap, r * rowGap);
      });
    } else {
      const radius = Number(
        settings.radius ?? radiusForCurve(columns, seatGap, 50),
      );
      if (!Number.isFinite(radius) || radius < MIN_ARC_RADIUS)
        throw new Error("Enter a radius of at least 40.");
      const { from, to } =
        settings.from && settings.to
          ? settings
          : curvedEdges(radius, seatGap, columns);
      const rowSeats = settings.rowSeats;
      // An old arc's one count per row is drawn as it was.
      if (
        typeof rowSeats !== "number" &&
        arcSpan({ ...settings, radius, from, to }) > wrapAllowed
      )
        throw new Error("Use fewer seats or a gentler curve.");
      const curved = arcSeats({
        radius,
        rows,
        rowSpacing: rowGap,
        seatSpacing: seatGap,
        from,
        to,
        rowSeats,
        rowPitch: settings.rowPitch,
      });
      if (curved.length > MAX_OBJECT_SEATS)
        throw new Error("Use at most 2,000 seats per object.");
      curved.forEach((s) =>
        place(s.row, s.index, s.count, tidy(s.x), tidy(s.y)),
      );
      block.radius = radius;
      block.from = from;
      block.to = to;
      block.columns =
        (Array.isArray(rowSeats) ? rowSeats[0] : rowSeats) ||
        arcRowCount(radius, from, to, seatGap);
    }
  } else if (shape === "round") {
    const chairs = count("chairs", 8),
      radius = Number(settings.radius ?? tableRadius(settings));
    if (!Number.isFinite(radius) || radius < 30)
      throw new Error("Enter a table radius of at least 30.");
    block.radius = radius;
    for (let c = 0; c < chairs; c++) {
      const a = settings.facing
        ? Math.PI * (chairs === 1 ? 0.5 : 0.15 + (0.7 * c) / (chairs - 1))
        : -Math.PI / 2 + (2 * Math.PI * c) / chairs;
      add(
        `c${c + 1}`,
        (radius + 30) * Math.cos(a),
        (radius + 30) * Math.sin(a),
        null,
        c + 1,
      );
    }
  } else if (shape === "banquet") {
    const side = count("chairsPerSide", 4);
    const ends = Number(settings.chairsPerEnd ?? 1);
    if (!Number.isInteger(ends) || ends < 0 || ends > 100)
      throw new Error("Enter a whole chairs per end count between 0 and 100.");
    const gap = Number(settings.seatSpacing ?? SEAT_PITCH);
    if (!Number.isFinite(gap) || gap < 30 || gap > 200)
      throw new Error("Enter a spacing between 30 and 200.");
    block.chairsPerEnd = ends;
    block.tableWidth = Math.max(76, side * gap);
    block.tableHeight = ends > 1 ? (ends + 1) * 38 : 60;
    for (let c = 0; c < side; c++) {
      add(`a${c + 1}`, c * gap, -block.tableHeight / 2 - 16, null, c + 1);
      add(`b${c + 1}`, c * gap, block.tableHeight / 2 + 16, null, side + c + 1);
    }
    for (let c = 0; c < ends; c++) {
      const y =
        -block.tableHeight / 2 + (block.tableHeight * (c + 1)) / (ends + 1);
      add(`c${c + 1}`, -54, y, null, side * 2 + c + 1);
      add(`d${c + 1}`, block.tableWidth + 16, y, null, side * 2 + ends + c + 1);
    }
  } else if (shape === "standing") {
    const capacity = count("capacity", 30, 2000),
      cols = Math.ceil(Math.sqrt(capacity));
    for (let c = 0; c < capacity; c++)
      add(
        `g${c + 1}`,
        (c % cols) * 38,
        Math.floor(c / cols) * 38,
        null,
        c + 1,
        "",
        String(c + 1),
      );
  } else if (shape === "single") add("s1", 0, 0, null, 1);
  else throw new Error("Choose a supported shape.");
  if (settings.sellAsPackage && !formID)
    throw new Error("Choose a ticket type for a package.");
  block.nextSeat = Math.max(
    Number(settings.nextSeat || 1),
    1 + Math.max(...seats.map((s) => seatNumber(s.id))),
  );
  return { block, seats };
}
// A seat ID ends in a number. What comes before it is the seat's group, a row
// or a side of a table, and `nextSeat` on the block is a number no seat in that
// block has ever had, so a removed ID never comes back on another seat.
export const seatGroup = (id) => id.replace(/-?\d+$/, "");
export const seatNumber = (id) => Number(id.match(/\d+$/)?.[0] ?? 0);
export function seatPoint(layout, seat) {
  const block = layout.blocks.find((b) => b.id === seat.blockId);
  if (!block) return { x: 0, y: 0 };
  const a = (block.rotation * Math.PI) / 180;
  return {
    x: block.x + seat.x * Math.cos(a) - seat.y * Math.sin(a),
    y: block.y + seat.x * Math.sin(a) + seat.y * Math.cos(a),
  };
}
export function seatsInRectangle(layout, from, to) {
  const x1 = Math.min(from.x, to.x),
    x2 = Math.max(from.x, to.x),
    y1 = Math.min(from.y, to.y),
    y2 = Math.max(from.y, to.y);
  return layout.seats
    .filter((seat) => {
      const p = seatPoint(layout, seat);
      return p.x >= x1 && p.x <= x2 && p.y >= y1 && p.y <= y2;
    })
    .map((s) => s.id);
}
export function mergeSelection(base, hits, additive) {
  return [...new Set([...(additive ? base : []), ...hits])];
}
export function arrangeBlocks(layout, blockIds, action) {
  const bounds = layout.blocks
    .filter((b) => blockIds.includes(b.id))
    .map((b) => {
      const points = layout.seats
        .filter((s) => s.blockId === b.id)
        .map((s) => seatPoint(layout, s));
      if (!points.length) return null;
      return {
        id: b.id,
        x: Math.min(...points.map((p) => p.x)),
        y: Math.min(...points.map((p) => p.y)),
        right: Math.max(...points.map((p) => p.x)),
        bottom: Math.max(...points.map((p) => p.y)),
      };
    })
    .filter(Boolean);
  if (bounds.length < 2)
    throw new Error("Select seats from at least two blocks.");
  const axis = action.endsWith("X") || action === "left" ? "x" : "y";
  const end = axis === "x" ? "right" : "bottom";
  bounds.sort((a, b) => a[axis] - b[axis]);
  const origin = bounds[0][axis],
    last = bounds[bounds.length - 1];
  const gap =
    (last[end] -
      origin -
      bounds.reduce((sum, b) => sum + b[end] - b[axis], 0)) /
    (bounds.length - 1);
  let cursor = origin;
  const delta = new Map(
    bounds.map((b) => {
      const d = (action.startsWith("distribute") ? cursor : origin) - b[axis];
      cursor += b[end] - b[axis] + gap;
      return [b.id, d];
    }),
  );
  return {
    ...layout,
    blocks: layout.blocks.map((b) =>
      delta.has(b.id) ? { ...b, [axis]: b[axis] + delta.get(b.id) } : b,
    ),
  };
}
function inventorySeatLabel(block, seat) {
  if (["round", "banquet"].includes(block.shape))
    return seat.label.startsWith("Chair ") ? seat.label : `Chair ${seat.label}`;
  return seat.label;
}
export function labelForSeat(layout, seatId, indexes) {
  const seat = indexes
      ? indexes.seats.get(seatId)
      : layout?.seats?.find((s) => s.id === seatId),
    block = indexes
      ? indexes.blocks.get(seat?.blockId)
      : layout?.blocks?.find((b) => b.id === seat?.blockId);
  return seat && block
    ? formatAssignment({
        groupLabel: block.name,
        rowLabel: seat.rowLabel,
        seatLabel:
          block.shape === "standing" ? "GA" : inventorySeatLabel(block, seat),
      })
    : "No seat selected";
}
export function assignmentForSeat(layout, seatId) {
  const seat = layout.seats.find((s) => s.id === seatId),
    block = layout.blocks.find((b) => b.id === seat?.blockId);
  if (!seat || !block) throw new Error("Seat does not exist.");
  return {
    seatID: seatId,
    packageID: seat.packageID || null,
    groupLabel: block.name,
    rowLabel: seat.rowLabel,
    seatLabel:
      block.shape === "standing" ? "GA" : inventorySeatLabel(block, seat),
  };
}
export function formatAssignment(assignment, r, c) {
  if (assignment?.seatMessage) return assignment.seatMessage;
  const group = assignment?.groupLabel;
  if (group)
    return [
      group,
      assignment.rowLabel ? `Row ${assignment.rowLabel}` : "",
      /:g[0-9]+$/.test(assignment.seatID || "")
        ? "GA"
        : assignment.seatLabel === "GA" ||
            assignment.seatLabel?.startsWith("Chair ")
          ? assignment.seatLabel
          : `Seat ${assignment.seatLabel}`,
    ]
      .filter(Boolean)
      .join(" · ");
  return r && c
    ? `Row ${rowLabel(Number(r) - 1)} Seat ${c}`
    : "No seat selected";
}
// The canvas uses relative chair positions; inventory always uses world coordinates.
export const seatFlags = ["wheelchair", "companion"];
// A draft can't name a type the server doesn't yet have on the map, so those
// seats go up without one and the chair remembers it until publish.
export function toSeatInput(layout, { draft = false } = {}) {
  return layout.seats.map((s) => {
    const waiting = draft && layout.pendingForms?.[s.formID];
    const b = layout.blocks.find((b) => b.id === s.blockId),
      p = seatPoint(layout, s);
    return {
      seatID: s.id,
      objectID: b.id,
      groupLabel: b.name,
      rowLabel: s.rowLabel || "",
      seatLabel: inventorySeatLabel(b, s),
      ...p,
      formID: waiting ? null : s.formID || null,
      packageID: waiting ? null : s.packageID || null,
      flags: seatFlags.filter((flag) => s[flag]),
    };
  });
}
export function toDrawing(layout) {
  const { blocks, markers, seats, ...rest } = layout;
  return {
    ...rest,
    objects: [
      ...blocks.map((b) => ({ ...b, objectID: b.id })),
      // `shape` marks the object as a decoration, so the drawn outline travels
      // in its own field.
      ...markers.map((m) => ({
        ...m,
        objectID: m.id,
        shape: "decoration",
        outline: m.shape,
      })),
    ],
    // Retain local chair offsets, manual labels and edits as part of the drawing.
    chairs: seats.map(
      ({ blocked, opensAt, reservationID, moved, published, ...chair }) => chair,
    ),
  };
}
// What buyers may read. Tags kept from them, the floor plan and changes waiting
// for publish stay with the organizer.
export const isFloorPlan = (marker) => marker.type === "underlay";
export function publicDrawing(layout) {
  const { tags = [], pendingForms, ...drawing } = toDrawing(layout);
  const shown = tags.filter((tag) => tag.buyers);
  const ids = new Set(shown.map((tag) => tag.id));
  return {
    ...drawing,
    ...(shown.length ? { tags: shown } : {}),
    objects: drawing.objects.filter((o) => !isFloorPlan(o)),
    chairs: drawing.chairs.map(({ tags: own, ...chair }) => {
      const kept = own?.filter((id) => ids.has(id));
      return kept?.length ? { ...chair, tags: kept } : chair;
    }),
  };
}
export const hasPrivateParts = (layout) =>
  layout.markers.some(isFloorPlan) || !!layout.tags?.some((tag) => !tag.buyers);
// Publishing overwrites the draft with what was published. This is the draft
// to save straight after, so the organizer's own parts come back.
export function withPrivateParts(drawing, layout) {
  const ours = layout.tags || [];
  const theirs = drawing.tags || [];
  const isPrivate = new Set(ours.filter((t) => !t.buyers).map((t) => t.id));
  const known = new Set(ours.map((t) => t.id));
  const own = new Map(
    layout.seats.filter((s) => s.tags?.length).map((s) => [s.id, s.tags]),
  );
  return {
    ...drawing,
    // The draft may be another organizer's by now: only what publishing took
    // off comes back, and what they changed stays.
    tags: [
      ...ours.flatMap((tag) => {
        const kept = theirs.find((t) => t.id === tag.id);
        return kept ? [kept] : isPrivate.has(tag.id) ? [tag] : [];
      }),
      ...theirs.filter((t) => !known.has(t.id)),
    ],
    objects: drawing.objects.some(isFloorPlan)
      ? drawing.objects
      : [...drawing.objects, ...toDrawing(layout).objects.filter(isFloorPlan)],
    chairs: drawing.chairs.map((c) => {
      const tags = c.tags || [];
      const kept = (own.get(c.id) || []).filter(
        (id) => isPrivate.has(id) || tags.includes(id),
      );
      const merged = [...new Set([...kept, ...tags])];
      return merged.length ? { ...c, tags: merged } : c;
    }),
  };
}
// What a buyer's map is made of, minus what the server and numbering add.
export function publicText(layout, ticketColors) {
  const sorted = (value) =>
    Array.isArray(value)
      ? value.map(sorted)
      : value && typeof value === "object"
        ? Object.fromEntries(
            Object.keys(value)
              .filter((k) => !["publishedVersion", "nextSeat"].includes(k))
              .sort()
              .map((k) => [k, sorted(value[k])]),
          )
        : value;
  const drawing = publicDrawing({
    ...layout,
    ticketColors: ticketColors ?? layout.ticketColors,
  });
  // A type added elsewhere has a colour but no seats, so buyers see no change.
  const used = new Set(drawing.chairs.map((c) => c.formID));
  drawing.ticketColors = Object.fromEntries(
    Object.entries(drawing.ticketColors || {}).filter(([id]) => used.has(id)),
  );
  return JSON.stringify(sorted(drawing));
}
const SEAT_FIELDS = [
  "objectID",
  "groupLabel",
  "rowLabel",
  "seatLabel",
  "formID",
  "packageID",
];
// Whether the map's seats are the live ones. Everything else in a drawing can
// change without touching a seat.
export function sameSeats(drawn, live) {
  if (drawn.length !== live.length) return false;
  const byID = new Map(live.map((s) => [s.seatID, s]));
  return drawn.every((s) => {
    const other = byID.get(s.seatID);
    return (
      !!other &&
      SEAT_FIELDS.every((key) => (s[key] ?? null) === (other[key] ?? null)) &&
      Math.abs(s.x - other.x) < 1 &&
      Math.abs(s.y - other.y) < 1 &&
      [...s.flags].sort().join() === [...(other.flags || [])].sort().join()
    );
  });
}
const COLOR = /^#[0-9a-f]{6}$/;
const FALLBACK_TAG_COLOR = "#475569";
// Colours reach the page as they stand, and buyers read this drawing.
const cleanColors = (drawing) => ({
  ...(drawing.ticketColors
    ? {
        ticketColors: Object.fromEntries(
          Object.entries(drawing.ticketColors).filter(([, c]) => COLOR.test(c)),
        ),
      }
    : {}),
  ...(drawing.tags
    ? {
        tags: drawing.tags.map((tag) =>
          COLOR.test(tag.color) ? tag : { ...tag, color: FALLBACK_TAG_COLOR },
        ),
      }
    : {}),
});
// Arcs drawn before concentric rows hang from their front row and spread one
// count over every row. Here they move to their focal point and hold that count
// until their shape is changed, so their seats stay where they are.
function concentricArc(block) {
  const { sweep = 90, radius = 160, columns, ...rest } = block;
  const turn = (Number(block.rotation || 0) * Math.PI) / 180;
  const n = Number(columns);
  const full = Number(sweep) === 360;
  return {
    ...rest,
    radius,
    columns: n,
    rowSeats: n,
    x: block.x + radius * Math.sin(turn),
    y: block.y - radius * Math.cos(turn),
    from: { deg: n === 1 ? 0 : -sweep / 2, px: 0 },
    to: { deg: n === 1 ? 0 : sweep / 2 - (full ? sweep / n : 0), px: 0 },
  };
}
export function fromDrawing(drawing, inventory = []) {
  if (!drawing) return emptyLayout();
  const lifted = new Map();
  const blocks = drawing.objects
    .filter((o) => o.shape !== "decoration")
    .map((o) => {
      const block = { ...o, id: o.objectID, skipIO: o.skipIO ?? false };
      if (block.shape !== "arc" || block.from) return block;
      const arc = concentricArc(block);
      lifted.set(arc.id, arc.radius);
      return arc;
    });
  const markers = drawing.objects
    .filter((o) => o.shape === "decoration")
    .map((o) => ({ ...o, id: o.objectID, shape: o.outline || "rectangle" }));
  const blocksByID = new Map(blocks.map((b) => [b.id, b]));
  const chairsByID = new Map((drawing.chairs || []).map((c) => [c.id, c]));
  const seats = inventory.length
    ? inventory.map((s) => {
        const b = blocksByID.get(s.objectID),
          a = (-(b?.rotation || 0) * Math.PI) / 180;
        const dx = s.x - (b?.x || 0),
          dy = s.y - (b?.y || 0);
        const chair = chairsByID.get(s.seatID);
        const waiting = !s.formID && drawing.pendingForms?.[chair?.formID];
        return {
          ...chair,
          id: s.seatID,
          blockId: s.objectID,
          rowLabel: s.rowLabel,
          label:
            ["round", "banquet"].includes(b?.shape) &&
            s.seatLabel.startsWith("Chair ")
              ? s.seatLabel.slice(6)
              : s.seatLabel,
          formID: waiting ? chair.formID : s.formID,
          packageID: waiting ? chair.packageID : s.packageID,
          blocked: s.blocked,
          opensAt: s.opensAt,
          reservationID: s.reservationID,
          ...Object.fromEntries(
            seatFlags.map((flag) => [flag, !!s.flags?.includes(flag)]),
          ),
          x:
            chair &&
            Math.abs(chair.x - (dx * Math.cos(a) - dy * Math.sin(a))) < 1e-6
              ? chair.x
              : dx * Math.cos(a) - dy * Math.sin(a),
          y:
            chair &&
            Math.abs(chair.y - (dx * Math.sin(a) + dy * Math.cos(a))) < 1e-6
              ? chair.y
              : dx * Math.sin(a) + dy * Math.cos(a),
        };
      })
    : (drawing.chairs || []).map((c) =>
        lifted.has(c.blockId) ? { ...c, y: c.y + lifted.get(c.blockId) } : c,
      );
  return { ...drawing, ...cleanColors(drawing), blocks, markers, seats };
}
export const inventoryByID = (seats) =>
  Object.fromEntries((seats || []).map((s) => [s.seatID, s]));
export const MIXED_TERMS = "These seats have different holds or opening times.";
// Package members share a hold and an opening time, or the server refuses them.
export function mixedTerms(seats) {
  const terms = (s) => [s.reservationID || "", s.opensAt || ""].join("/");
  return seats.some((s) => terms(s) !== terms(seats[0]));
}
export function packageSelection(layout, ids, size) {
  const chosen = layout.seats.filter((s) => ids.includes(s.id));
  if (
    !chosen.length ||
    chosen.some((s) => !s.formID || s.formID !== chosen[0].formID)
  )
    throw new Error("A package needs one explicit ticket type for all seats.");
  if (mixedTerms(chosen)) throw new Error(MIXED_TERMS);
  if (
    chosen.some(
      (s) =>
        s.packageID &&
        layout.seats.some(
          (other) => other.packageID === s.packageID && !ids.includes(other.id),
        ),
    )
  )
    throw new Error("Select the whole existing package before regrouping it.");
  size = Number(size || chosen.length);
  if (!Number.isInteger(size) || size < 2 || chosen.length % size)
    throw new Error("Choose a group size that divides the selection evenly.");
  const sorted = [...chosen].sort(
    (a, b) =>
      a.blockId.localeCompare(b.blockId) ||
      a.row - b.row ||
      a.column - b.column,
  );
  const packages = new Map(
    sorted.map((s, i) => [s.id, `${sorted[Math.floor(i / size) * size].id}:p`]),
  );
  return {
    ...layout,
    seats: layout.seats.map((s) =>
      packages.has(s.id) ? { ...s, packageID: packages.get(s.id) } : s,
    ),
  };
}
// Spacing can go down to 30, and a curve brings seats a little closer than their
// spacing along the row.
const MIN_SEAT_GAP = 29;
export const REPEATED_LABELS =
  "Seat labels must be nonempty and unique within their block and row.";
export const OUTSIDE_ROOM = "Keep every seat inside the canvas.";
export function validateLayout(layout, previous, inventory = {}) {
  const errors = [],
    ids = new Set(),
    labels = new Set(),
    blockNames = new Set();
  const positions = new Map();
  if (!layout?.name?.trim()) errors.push("Give the layout a name.");
  if (
    !Number.isFinite(layout?.width) ||
    !Number.isFinite(layout?.height) ||
    layout.width < 200 ||
    layout.height < 200 ||
    layout.width > 10000 ||
    layout.height > 10000
  )
    errors.push("Canvas dimensions must be between 200 and 10,000.");
  if (!layout?.blocks?.length || !layout?.seats?.length)
    errors.push("Add at least one seating block and seat.");
  for (const block of layout.blocks || []) {
    if (!block.name?.trim() || blockNames.has(block.name?.trim().toLowerCase()))
      errors.push("Block names must be unique and nonempty.");
    blockNames.add(block.name?.trim().toLowerCase());
    if (ids.has(block.id)) errors.push("Duplicate object ID.");
    ids.add(block.id);
    if (![block.x, block.y, block.rotation].every(Number.isFinite))
      errors.push("Invalid block geometry.");
  }
  for (const seat of layout.seats || []) {
    if (ids.has(seat.id)) errors.push("Duplicate object ID.");
    ids.add(seat.id);
    if (!layout.blocks.some((b) => b.id === seat.blockId))
      errors.push("A seat belongs to a missing block.");
    const key = `${seat.blockId}/${seat.rowLabel?.trim()}/${seat.label?.trim()}`;
    if (!seat.label?.trim() || labels.has(key.toLowerCase()))
      errors.push(REPEATED_LABELS);
    labels.add(key.toLowerCase());
    if (![seat.x, seat.y].every(Number.isFinite))
      errors.push("Invalid seat position.");
    const p = seatPoint(layout, seat);
    const gx = Math.floor(p.x / 30),
      gy = Math.floor(p.y / 30);
    for (let dx = -1; dx <= 1; dx++)
      for (let dy = -1; dy <= 1; dy++)
        for (const other of positions.get(`${gx + dx}:${gy + dy}`) || [])
          if (Math.hypot(p.x - other.x, p.y - other.y) < MIN_SEAT_GAP)
            errors.push("Seats overlap. Move blocks or increase seat spacing.");
    const cell = `${gx}:${gy}`;
    positions.set(cell, [...(positions.get(cell) || []), p]);
    if (
      p.x < 18 ||
      p.y < 18 ||
      p.x > layout.width - 18 ||
      p.y > layout.height - 18
    )
      errors.push(OUTSIDE_ROOM);
  }
  for (const marker of layout.markers || []) {
    if (ids.has(marker.id)) errors.push("Duplicate object ID.");
    ids.add(marker.id);
    if (
      (marker.type !== "custom" && !marker.text?.trim()) ||
      ![marker.x, marker.y].every(Number.isFinite)
    )
      errors.push("Markers need text and a valid position.");
    if (isFloorPlan(marker)) continue;
    if (
      (marker.width !== undefined &&
        (!Number.isFinite(marker.width) ||
          marker.width < 48 ||
          marker.width > 2000)) ||
      (marker.height !== undefined &&
        (!Number.isFinite(marker.height) ||
          marker.height < 32 ||
          marker.height > 1000))
    )
      errors.push("Marker dimensions must be 48–2,000 wide and 32–1,000 high.");
  }
  if (
    layout.seats.length > 20000 ||
    layout.blocks.length + layout.markers.length > 500
  )
    errors.push("Use at most 20,000 seats and 500 objects.");
  if (
    new TextEncoder().encode(JSON.stringify(toDrawing(layout))).length > 1048576
  )
    errors.push("The drawing must be smaller than 1 MB.");
  for (const b of layout.blocks)
    if (!/^[A-Za-z0-9_-]{1,40}$/.test(b.id) || b.name.length > 60)
      errors.push("Object IDs or labels exceed the map limits.");
  const packages = new Map();
  for (const s of layout.seats) {
    if (
      !/^[A-Za-z0-9:_-]{1,40}$/.test(s.id) ||
      !s.id.startsWith(s.blockId + ":")
    )
      errors.push(
        "Seat IDs must use their object's stable ID prefix and at most 40 characters.",
      );
    if ((s.rowLabel || "").length > 60 || s.label.length > 60)
      errors.push("Seat labels must be at most 60 characters.");
    if (s.packageID) {
      const key = [s.formID, s.reservationID || null, s.opensAt || null].join(
        "/",
      );
      if (
        !s.formID ||
        (packages.has(s.packageID) && packages.get(s.packageID) !== key)
      )
        errors.push(
          "Each package needs one explicit ticket type, reservation and opening time.",
        );
      packages.set(s.packageID, key);
    }
  }
  return [...new Set(errors)];
}
export function isOccupied(item, now = Date.now()) {
  return (
    !!item?.submissionID ||
    (!!item?.holdSubmissionID && Number(item.holdExpires) * 1000 > now)
  );
}
export function seatState(item = {}, { formID } = {}) {
  if (formID && item.formID && item.formID !== formID) return "unavailable";
  return (
    { mine: "selected", reserved: "restricted" }[item.state] ||
    item.state ||
    "available"
  );
}
export function duplicateBlock(layout, blockId) {
  const original = layout.blocks.find((b) => b.id === blockId);
  if (!original) throw new Error("Choose a block first.");
  const names = new Set(layout.blocks.map((b) => b.name));
  let name = `${original.name} copy`,
    n = 2;
  while (names.has(name)) name = `${original.name} copy ${n++}`;
  const points = layout.seats
    .filter((s) => s.blockId === blockId)
    .map((s) => seatPoint(layout, s));
  const offset = points.length
    ? Math.max(...points.map((p) => p.x)) -
      Math.min(...points.map((p) => p.x)) +
      76
    : 76;
  const copyID = uid("obj");
  const packages = new Map();
  const block = {
    ...original,
    objectID: copyID,
    id: copyID,
    name,
    x: original.x + offset,
    y: original.y,
  };
  return {
    ...layout,
    blocks: [...layout.blocks, block],
    seats: [
      ...layout.seats,
      ...layout.seats
        .filter((s) => s.blockId === blockId)
        .map(({ published, ...s }) => ({
          ...s,
          id: block.id + s.id.slice(blockId.length),
          blockId: block.id,
          packageID: s.packageID
            ? packages.get(s.packageID) ||
              (packages.set(s.packageID, uid("pkg")), packages.get(s.packageID))
            : null,
          blocked: 0,
          opensAt: null,
          reservationID: null,
        })),
    ],
  };
}
